import { Key } from 'react';
import {
  DataTableState,
  DataTableAction,
  DataTableRowsType,
  DataTableRowSelectionRecord,
  DataTableRowType,
} from './types';

function isSelectableRow<DataType>(row: DataTableRowType<DataType>): boolean {
  return !row.disabled && (row.selectable ?? true);
}

function getSelectableRows<DataType>(
  rows: DataTableRowsType<DataType>,
  rowSelectionKeyExtractor: (row: DataType, index: number) => Key,
): DataTableRowSelectionRecord<DataType>[] {
  return rows
    .map((row, index) => ({ row, index, key: rowSelectionKeyExtractor(row, index) }))
    .filter(({ row }) => isSelectableRow(row));
}

export interface DataTableReducerArgs<DataType> {
  rowSelectionKeyExtractor: (row: DataType, index: number) => Key;
}

export function dataTableReducer<DataType>(args: DataTableReducerArgs<DataType>) {
  const { rowSelectionKeyExtractor } = args;
  return function (state: DataTableState<DataType>, action: DataTableAction<DataType>): DataTableState<DataType> {
    switch (action.type) {
      case 'SELECT_ROWS': {
        const { rows } = action;
        const selectableRows = getSelectableRows(rows, rowSelectionKeyExtractor);

        if (!state.autoResetRowSelection) {
          const allSelectableRowsChecked = selectableRows.every((item) => state.selectedRows?.has(item.key));
          const selectedRows = new Map(state.selectedRows);
          if (!allSelectableRowsChecked) {
            selectableRows.forEach((item) => selectedRows.set(item.key, item));
          } else {
            selectableRows.forEach((item) => selectedRows.delete(item.key));
          }

          return {
            ...state,
            allSelected: !allSelectableRowsChecked,
            selectedRows,
            emitSelectedEvent: true,
          };
        }
        const shouldAllRowChecked = !state.allSelected;
        return {
          ...state,
          allSelected: shouldAllRowChecked,
          selectedRows: new Map(shouldAllRowChecked ? selectableRows.map((item) => [item.key, item]) : []),
          emitSelectedEvent: true,
        };
      }
      case 'EXPAND_ROW': {
        const { expanded, row, index } = action;
        const rowKey = rowSelectionKeyExtractor(row, index);
        if (row.disabled || !(row.expandable ?? true)) {
          return state;
        }

        if (expanded) {
          const filteredRows = state.expandedRows?.filter((key) => key !== rowKey);
          return {
            ...state,
            expandedRows: filteredRows,
            emitExpandedEvent: true,
          };
        }

        return {
          ...state,
          expandedRows: [...(state.expandedRows ? state.expandedRows : []), rowKey],
          emitExpandedEvent: true,
        };
      }
      case 'EXPAND_CONTROLLED_ROW': {
        const { rows, expandedRows } = action;
        return {
          ...state,
          expandedRows: expandedRows
            .map(({ row, index }) => rowSelectionKeyExtractor(row, index))
            .filter((key) => {
              const row = rows.find((row, index) => rowSelectionKeyExtractor(row, index) === key);
              return row && !row.disabled && (row.expandable ?? true);
            }),
          emitExpandedEvent: false,
        };
      }
      case 'SELECT_ROW': {
        const { rows, selected, row, index } = action;
        const rowKey = rowSelectionKeyExtractor(row, index);
        if (row.disabled || !(row.selectable ?? true)) {
          return state;
        }

        if (selected) {
          const deleteSelectedRows = new Map(state.selectedRows);
          deleteSelectedRows.delete(rowKey);
          return {
            ...state,
            allSelected: false,
            selectedRows: deleteSelectedRows,
            emitSelectedEvent: true,
          };
        }
        const selectableRows = getSelectableRows(rows, rowSelectionKeyExtractor);
        const addSelectedRows = new Map(state.selectedRows);
        addSelectedRows.set(rowKey, { row, index, key: rowKey });
        return {
          ...state,
          allSelected: selectableRows.every((item) => item.key && addSelectedRows?.has(item.key)),
          selectedRows: addSelectedRows,
          emitSelectedEvent: true,
        };
      }
      case 'SELECT_CONTROLLED_ROW': {
        const { controlledRows, rows } = action;
        const selectableRows = getSelectableRows(rows, rowSelectionKeyExtractor);
        const selectableControlledRows = new Map(
          controlledRows
            .filter(({ row }) => isSelectableRow(row))
            .map(({ row, index }) => ({ row, index, key: rowSelectionKeyExtractor(row, index) }))
            .map((item) => [item.key, item]),
        );
        return {
          ...state,
          allSelected:
            selectableControlledRows.size === 0 && selectableRows.length === 0
              ? false
              : selectableRows.every((row) => selectableControlledRows.has(row.key)),
          selectedRows: selectableControlledRows,
          emitSelectedEvent: false,
        };
      }
      case 'RESET_SELECTED_ROWS': {
        return {
          ...state,
          allSelected: false,
          selectedRows: new Map(),
          emitSelectedEvent: true,
        };
      }
      case 'CHECK_ALL_SELECTED_ROWS': {
        const { rows } = action;
        const selectableRows = getSelectableRows(rows, rowSelectionKeyExtractor);
        return {
          ...state,
          allSelected: selectableRows.every((item) => item.key && state.selectedRows?.has(item.key)),
          emitSelectedEvent: false,
        };
      }
      default: {
        throw Error('Unknown action: ' + action);
      }
    }
  };
}
